// 📁 src/HomeGlobal.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CurrencyContext } from "./context/CurrencyContext";
import { Link } from "react-router-dom";
import { API_BASE } from "./apiBase";

// ✅ Helper: normalize image URLs from DB/multer/CDN/public safely
function buildImageUrl(img) {
  if (!img || typeof img !== "string") return "/placeholder-product.png";

  // already absolute
  if (img.startsWith("http://") || img.startsWith("https://")) return img;

  // backend uploads (stored like "/uploads/xxx.jpg" or "uploads/xxx.jpg")
  if (img.startsWith("/uploads")) return `${API_BASE}${img}`;
  if (img.startsWith("uploads/")) return `${API_BASE}/${img}`;

  // public assets ("/icons/..", "/foo.png", etc)
  return img;
}

export default function HomeGlobal() {
  const { currency, rates } = useContext(CurrencyContext);

  const symbolMap = {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
    AED: "د.إ",
  };

  const fallbackRates = {
    INR: 1,
    USD: 0.012,
    EUR: 0.011,
    GBP: 0.0098,
    AED: 0.044,
  };

  const rate = rates?.[currency] || fallbackRates[currency];

  // --- NEW: fetch one representative image per category for the homepage cards
  const [categoryImage, setCategoryImage] = useState({
    raw: "",
    roasted: "",
    flavored: "",
    giftpacks: "",
  });

  const [compareSnack, setCompareSnack] = useState("chips");

  // ✅ Bulletproof backend fetch for homepage category images
  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    (async () => {
      try {
        const url = `${API_BASE}/api/products`;

        const res = await fetch(url, {
          cache: "no-store",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });

        // ✅ If backend returns HTML / error, don't call res.json()
        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            `Home products fetch failed: ${res.status} ${res.statusText}\n` +
              `URL: ${url}\n` +
              `Response preview: ${text.slice(0, 250)}`
          );
        }

        const data = await res.json();
        const list = Array.isArray(data) ? data : [];

        // group first image per category
        const bucket = { raw: "", roasted: "", flavored: "", giftpacks: "" };

        for (const p of list) {
          const cat = String(p.category || "").toLowerCase().trim();

          if (cat === "raw" && !bucket.raw) bucket.raw = p.image || "";
          if (cat === "roasted" && !bucket.roasted) bucket.roasted = p.image || "";
          if (cat === "flavored" && !bucket.flavored) bucket.flavored = p.image || "";

          // ✅ tolerate "gift", "gift pack", "gift packs", etc.
          if (cat.includes("gift") && !bucket.giftpacks) bucket.giftpacks = p.image || "";
        }

        if (isMounted) setCategoryImage(bucket);
      } catch (e) {
        // Abort is not a real error
        if (e?.name === "AbortError") return;
        console.error("Home: failed to fetch products for images", e);
      }
    })();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Your pricing cards (kept as-is)
  const products = useMemo(
    () => [
      { key: "raw", name: "Raw", price: 450, image: categoryImage.raw },
      { key: "roasted", name: "Roasted", price: 520, image: categoryImage.roasted },
      { key: "flavored", name: "Flavored", price: 590, image: categoryImage.flavored },
      { key: "giftpacks", name: "Gift Packs", price: 990, image: categoryImage.giftpacks },
    ],
    [categoryImage]
  );

  return (
    <div className="bg-[#f3f0e4] text-[#105132]">
      {/* Hero Section */}
      <section className="relative min-h-[72vh] sm:min-h-[80vh] flex items-center justify-center bg-[#105132]">
        {/* ✅ Background image as <img> (better for LCP + consistent) */}
        <img
          src="/hero-makhana.webp"
          alt="Premium makhana in bowl"
          className="absolute inset-0 w-full h-full object-cover"
          fetchpriority="high"
          decoding="async"
        />

        {/* ✅ Dark overlay for readability */}
        <div className="absolute inset-0 bg-[#105132]/55" />

        <motion.div
          className="relative z-10 text-center px-4 sm:px-6 text-white"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 drop-shadow-[0_6px_18px_rgba(0,0,0,0.45)]">
            Sourcing Nature, Shipping Purity
          </h2>

          <p className="mb-6 text-base sm:text-lg font-semibold text-white/95 drop-shadow-[0_4px_10px_rgba(0,0,0,0.35)]">
            FoxNuts Retailers & Exporters • Rooted in Bihar
          </p>

          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <Link to="/products#raw">
              <button className="w-full sm:w-auto bg-[#c0a230] text-[#105132] px-6 py-2 rounded-xl font-semibold transition hover:bg-[#e0b735] hover:shadow-lg">
                Explore Products
              </button>
            </Link>

            <button className="w-full sm:w-auto border border-white text-white px-6 py-2 rounded-xl font-semibold transition hover:bg-white hover:text-[#105132]">
              Become a Distributor
            </button>
          </div>
        </motion.div>
      </section>

      {/* Our Products */}
      <section id="products" className="py-14 sm:py-20 px-4 sm:px-6 bg-[#f3f0e4] text-[#105132]">
        <motion.h3
          className="text-3xl sm:text-4xl font-bold text-center mb-10 sm:mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          Our Products
        </motion.h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {products.map((product, i) => {
            const converted = (product.price * rate).toFixed(2);

            return (
              <motion.div
                key={product.key}
                className="bg-white rounded-2xl shadow-lg group hover:shadow-2xl transition-all duration-300 border border-[#c0a230] overflow-hidden flex flex-col"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="bg-[#f3f0e4] p-4 flex justify-center items-center">
                  <img
                    src={buildImageUrl(product.image)}
                    alt={`${product.name} Makhana`}
                    className="h-36 sm:h-40 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/placeholder-product.png";
                    }}
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col items-center justify-center text-center px-5 py-6 flex-grow">
                  <h4 className="text-xl font-semibold text-[#105132] mb-2">
                    {product.name} Makhana
                  </h4>
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                    Handpicked, hygienically processed, and packed with purity. A healthy snack for global taste.
                  </p>
                  <p className="text-lg font-bold text-[#105132] mb-4">
                    {symbolMap[currency]} {converted}
                  </p>
                  <Link to={`/products#${product.key}`}>
                    <button className="bg-gradient-to-r from-[#c0a230] to-[#e3c75a] text-[#105132] px-6 py-2 rounded-full font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
                      Explore &rarr;
                    </button>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Why Nutrafox */}
      <section
        className="bg-[#f6f3ea] py-14 sm:py-20 px-4 sm:px-6 relative"
        style={{
          backgroundImage: "url('/texture/soft-paper.png')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            className="mb-10 sm:mb-12"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <div className="w-28 h-1 bg-[#c0a230] mx-auto mb-2 rounded-full"></div>
            <h3 className="text-3xl sm:text-4xl font-bold text-[#105132]">Why Nutrafox?</h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                title: "100% Natural",
                icon: "/icons/natural.png",
                desc: "Pure, unadulterated makhana sourced directly from nature.",
              },
              {
                title: "Sourced from Bihar",
                icon: "/icons/bihar.png",
                desc: "Authentic makhana harvested from the heartland of Bihar.",
              },
              {
                title: "FSSAI & APEDA Certified",
                icon: "/icons/certified.png",
                desc: "Meeting the highest standards of quality and safety.",
              },
              {
                title: "Export-Ready Supply Chain",
                icon: "/icons/export.png",
                desc: "Efficient logistics ensuring timely global deliveries.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="flex flex-col items-center p-6 bg-[#fdf8ee] text-[#105132] border border-[#236e47] rounded-2xl shadow-md hover:scale-[1.02] hover:shadow-xl transition text-center"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.img
                  src={item.icon}
                  alt={item.title}
                  className="h-12 w-12 mb-4 bg-white p-2 rounded-full shadow-sm border border-[#c0a230]"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                />
                <h4 className="text-xl font-semibold mb-2">{item.title}</h4>
                <p className="text-sm opacity-90">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modern Nutrition Section */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 bg-[#f3f0e4]">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#c0a230]/50 bg-white/70 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-[#c0a230]" />
              <span className="text-sm font-semibold text-[#105132]">
                Smart Snacking, Upgraded
              </span>
            </div>

            <h3 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#105132] tracking-tight">
              Why Nutritionists Recommend Makhana
            </h3>
            <p className="mt-3 text-[#105132] opacity-80 max-w-2xl mx-auto">
              Clean energy, light on the stomach, and naturally better than most everyday snacks.
            </p>
          </motion.div>

          {/* 4 sleek benefit cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {[
              {
                title: "Low Glycemic",
                desc: "Steady energy, fewer spikes",
                icon: (
                  <svg viewBox="0 0 24 24" className="h-6 w-6">
                    <path
                      fill="currentColor"
                      d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm1 11.2V7h-2v7l5 3 .9-1.6Z"
                    />
                  </svg>
                ),
              },
              {
                title: "Plant Protein",
                desc: "~9–10g per 100g",
                icon: (
                  <svg viewBox="0 0 24 24" className="h-6 w-6">
                    <path
                      fill="currentColor"
                      d="M12 2C8.7 2 6 4.7 6 8c0 4.3 6 14 6 14s6-9.7 6-14c0-3.3-2.7-6-6-6Zm0 8.5A2.5 2.5 0 1 1 14.5 8 2.5 2.5 0 0 1 12 10.5Z"
                    />
                  </svg>
                ),
              },
              {
                title: "Zero Cholesterol",
                desc: "Heart-friendly choice",
                icon: (
                  <svg viewBox="0 0 24 24" className="h-6 w-6">
                    <path
                      fill="currentColor"
                      d="M12 21s-7-4.6-9.3-9A5.7 5.7 0 0 1 12 6.7 5.7 5.7 0 0 1 21.3 12C19 16.4 12 21 12 21Z"
                    />
                  </svg>
                ),
              },
              {
                title: "Antioxidant Rich",
                desc: "Supports recovery & immunity",
                icon: (
                  <svg viewBox="0 0 24 24" className="h-6 w-6">
                    <path
                      fill="currentColor"
                      d="M12 2l3 7h7l-5.7 4.1L18.5 21 12 16.9 5.5 21l2.2-7.9L2 9h7Z"
                    />
                  </svg>
                ),
              },
            ].map((b, i) => (
              <motion.div
                key={b.title}
                className="group rounded-3xl border border-[#c0a230]/40 bg-white/70 backdrop-blur p-6 shadow-sm hover:shadow-xl transition-all"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.7 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-2xl bg-[#105132] text-white flex items-center justify-center shadow-md">
                    {b.icon}
                  </div>
                  <span className="text-xs font-semibold text-[#105132]/70 px-3 py-1 rounded-full border border-[#105132]/10 bg-white">
                    NutraFact
                  </span>
                </div>

                <h4 className="mt-4 text-lg font-extrabold text-[#105132]">
                  {b.title}
                </h4>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">{b.desc}</p>

                <div className="mt-5 h-[2px] w-full bg-gradient-to-r from-[#c0a230] via-[#105132] to-transparent opacity-60" />
              </motion.div>
            ))}
          </div>

          {/* Comparison block */}
          <motion.div
            className="rounded-3xl border border-[#c0a230]/40 bg-white/70 backdrop-blur shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="p-5 sm:p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h4 className="text-2xl md:text-3xl font-extrabold text-[#105132]">
                    Compare with popular snacks
                  </h4>
                  <p className="mt-2 text-sm text-gray-600">
                    Choose a snack to compare. Values are approx per 100g and can vary by brand & preparation.
                  </p>
                </div>

                {/* Snack Switcher (mobile scroll) */}
                <div className="flex gap-2 overflow-x-auto whitespace-nowrap pb-2 -mx-1 px-1">
                  {[
                    { key: "chips", label: "Potato Chips" },
                    { key: "popcorn", label: "Butter Popcorn" },
                    { key: "namkeen", label: "Namkeen/Mixture" },
                    { key: "peanuts", label: "Salted Peanuts" },
                    { key: "biscuits", label: "Biscuits" },
                    { key: "noodles", label: "Instant Noodles" },
                  ].map((x) => (
                    <button
                      key={x.key}
                      onClick={() => setCompareSnack(x.key)}
                      className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                        compareSnack === x.key
                          ? "bg-[#105132] text-white border-[#105132] shadow-md"
                          : "bg-white text-[#105132] border-[#105132]/20 hover:border-[#c0a230] hover:shadow-sm"
                      }`}
                    >
                      {x.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Data */}
              {(() => {
                const base = {
                  makhana: { calories: "347 kcal", protein: "9.7 g", fat: "0.1 g", sodium: "Very Low", note: "Light, clean & balanced." },
                  chips: { calories: "536 kcal", protein: "7 g", fat: "37 g", sodium: "High", note: "Deep-fried + high salt." },
                  popcorn: { calories: "387 kcal", protein: "11 g", fat: "28 g", sodium: "High", note: "Butter/oil bumps fat up." },
                  namkeen: { calories: "500+ kcal", protein: "6–8 g", fat: "30–35 g", sodium: "Very High", note: "Often fried + salty." },
                  peanuts: { calories: "567 kcal", protein: "26 g", fat: "49 g", sodium: "Medium", note: "High protein but very high fat." },
                  biscuits: { calories: "450–520 kcal", protein: "5–8 g", fat: "15–25 g", sodium: "Medium", note: "Refined flour + sugar." },
                  noodles: { calories: "440–480 kcal", protein: "8–10 g", fat: "15–20 g", sodium: "Very High", note: "Ultra-processed + sodium heavy." },
                };

                const s = base[compareSnack];

                const parseNumber = (val) => {
                  if (!val) return null;
                  const str = String(val).replace(/,/g, "").trim();

                  const rangeMatch = str.match(/(\d+(\.\d+)?)\s*[–-]\s*(\d+(\.\d+)?)/);
                  if (rangeMatch) {
                    const lo = parseFloat(rangeMatch[1]);
                    const hi = parseFloat(rangeMatch[3]);
                    if (!Number.isNaN(lo) && !Number.isNaN(hi)) return (lo + hi) / 2;
                  }

                  const numMatch = str.match(/(\d+(\.\d+)?)/);
                  if (!numMatch) return null;

                  const n = parseFloat(numMatch[1]);
                  return Number.isNaN(n) ? null : n;
                };

                const sodiumRank = (val) => {
                  if (!val) return null;
                  const v = String(val).toLowerCase();
                  if (v.includes("very low")) return 1;
                  if (v.includes("low")) return 2;
                  if (v.includes("medium")) return 3;
                  if (v.includes("high") && !v.includes("very high")) return 4;
                  if (v.includes("very high")) return 5;
                  return null;
                };

                const betterSide = (metricKey, makhanaVal, snackVal) => {
                  if (metricKey === "sodium") {
                    const m = sodiumRank(makhanaVal);
                    const sn = sodiumRank(snackVal);
                    if (m == null || sn == null || m === sn) return null;
                    return m < sn ? "makhana" : "snack";
                  }

                  const m = parseNumber(makhanaVal);
                  const sn = parseNumber(snackVal);
                  if (m == null || sn == null || m === sn) return null;

                  if (metricKey === "protein") return m > sn ? "makhana" : "snack";
                  return m < sn ? "makhana" : "snack";
                };

                const Badge = () => (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-[#105132] text-white shadow-sm">
                    ✅ Better choice
                  </span>
                );

                const Row = ({ metricKey, label, a, b }) => {
                  const winner = betterSide(metricKey, a, b);

                  return (
                    <div className="grid grid-cols-12 gap-4 items-center py-3">
                      <div className="col-span-12 md:col-span-4 text-sm font-semibold text-[#105132]">
                        {label}
                      </div>

                      <div className="col-span-12 md:col-span-4">
                        <div className="rounded-2xl bg-green-50 border border-green-200 px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs text-green-700 font-semibold">Makhana</div>
                            {winner === "makhana" ? <Badge /> : null}
                          </div>
                          <div className="text-base font-extrabold text-green-800">{a}</div>
                        </div>
                      </div>

                      <div className="col-span-12 md:col-span-4">
                        <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs text-rose-700 font-semibold">Selected snack</div>
                            {winner === "snack" ? <Badge /> : null}
                          </div>
                          <div className="text-base font-extrabold text-rose-800">{b}</div>
                        </div>
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="mt-8">
                    <div className="rounded-3xl border border-[#105132]/10 bg-white p-4 sm:p-5 md:p-6">
                      <Row metricKey="calories" label="Calories" a={base.makhana.calories} b={s.calories} />
                      <div className="h-px bg-[#105132]/10" />
                      <Row metricKey="protein" label="Protein" a={base.makhana.protein} b={s.protein} />
                      <div className="h-px bg-[#105132]/10" />
                      <Row metricKey="fat" label="Fat" a={base.makhana.fat} b={s.fat} />
                      <div className="h-px bg-[#105132]/10" />
                      <Row metricKey="sodium" label="Sodium" a={base.makhana.sodium} b={s.sodium} />

                      <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold text-[#105132]">Quick insight:</span>{" "}
                          {s.note}
                        </p>

                        <Link
                          to="/nutrition"
                          className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#c0a230] text-[#105132] font-semibold hover:bg-[#e3c75a] transition shadow-sm"
                        >
                          View detailed nutrition facts <span aria-hidden>→</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partner With Us */}
      <section id="enquiry" className="py-14 sm:py-20 px-4 sm:px-6 text-center bg-[#fdf8ee]">
        <motion.h3
          className="text-3xl sm:text-4xl font-bold mb-4 text-[#105132]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          Partner With Us
        </motion.h3>
        <motion.p
          className="mb-8 text-base sm:text-lg text-[#105132]"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1 }}
        >
          Join hands with India’s finest Makhana exporters. Let’s grow global, together.
        </motion.p>

        <Link to="/export-enquiry">
          <motion.button
            className="w-full sm:w-auto bg-[#c0a230] hover:bg-[#e3c75a] text-[#105132] px-8 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Send Export Enquiry &rarr;
          </motion.button>
        </Link>
      </section>
    </div>
  );
}
